import { ZoneRow, AvailableZone, APILocationData } from './types';

/**
 * Extract unique VP_Zone values from API data to populate available zones
 */
export const getAvailableZonesFromData = (apiData: any[], type: 'DSD' | 'NON-DSD' = 'DSD') => {
  if (!apiData || apiData.length === 0) {
    // Default zones based on type
    if (type === 'NON-DSD') {
      return [
        { id: 'transfull', name: 'TRANSFULL' },
        { id: 'transpick', name: 'TRANSPICK' },
        { id: 'replen', name: 'REPLEN' },
        { id: 'receiving', name: 'RECEIVING' },
      ];
    } else {
      // DSD zones
      return [
        { id: 'geo', name: 'GEO' },
        { id: 'bay', name: 'BAY' },
        { id: 'bulk', name: 'BULK' },
        { id: 'full', name: 'FULL' },
        { id: 'layer', name: 'LAYER' },
        { id: 'chilled', name: 'CHILLED' },
        { id: 'support', name: 'SUPPORT' },
        { id: 'fsv', name: 'FSV' },
      ];
    }
  }

  // Check if data is API format (has VP_Zone property)
  const isAPIData = apiData.length > 0 && 'VP_Zone' in apiData[0];
  
  if (isAPIData) {
    // Extract unique VP_Zone values from API data
    const uniqueZones = [...new Set((apiData as APILocationData[]).map(item => item.VP_Zone))];
    return uniqueZones.map(zone => ({
      id: zone.toLowerCase(),
      name: zone
    }));
  } else {
    // Data is already in ZoneRow format
    const zoneRows = apiData as ZoneRow[];
    return zoneRows.map(zone => ({
      id: zone.id,
      name: zone.zoneName
    }));
  }
};

/**
 * Type guard to check if data is API format
 */
export const isAPIDataFormat = (data: any[]): data is APILocationData[] => {
  return data.length > 0 && 'VP_Zone' in data[0];
};

/**
 * Helper function to check if a zone should be hidden based on default_pct and default_kcph values.
 * A zone is only hidden when BOTH default_pct AND default_kcph are null/zero for every day,
 * so new roles that have 0 pct but a valid kcph (or vice versa) are still displayed.
 */
const shouldHideZone = (zoneItems: APILocationData[]): boolean => {
  return zoneItems.every(item => {
    const pctEmpty = item.default_pct === null || item.default_pct === 0;
    const kcphEmpty = item.default_kcph === null || item.default_kcph === 0;
    return pctEmpty && kcphEmpty;
  });
};

/**
 * Convert API data to ZoneRow format with proper mapping
 */
export const convertAPIDataToZoneRows = (apiData: APILocationData[]): ZoneRow[] => {
  // Group data by VP_Zone
  const zoneGroups = apiData.reduce((acc, item) => {
    const zoneKey = item.VP_Zone.toLowerCase();
    if (!acc[zoneKey]) {
      acc[zoneKey] = [];
    }
    acc[zoneKey].push(item);
    return acc;
  }, {} as Record<string, APILocationData[]>);

  // Convert each zone group to ZoneRow
  return Object.entries(zoneGroups)
    .map(([zoneId, zoneItems]) => {
      const zoneName = zoneItems[0].VP_Zone;
      
      // Initialize percentages and KCPH with default values (2 decimal places)
      const percentages = {
        sun: { default: '0.00%', override: '0.00%' },
        mon: { default: '0.00%', override: '0.00%' },
        tue: { default: '0.00%', override: '0.00%' },
        wed: { default: '0.00%', override: '0.00%' },
        thu: { default: '0.00%', override: '0.00%' },
        fri: { default: '0.00%', override: '0.00%' },
        sat: { default: '0.00%', override: '0.00%' },
      };
      
      let kcph = { default: '0.00', override: '0.00' };

      // Map day_num to day names (1=Sunday, 2=Monday, etc.)
      const dayMapping = {
        1: 'sun',
        2: 'mon', 
        3: 'tue',
        4: 'wed',
        5: 'thu',
        6: 'fri',
        7: 'sat'
      } as const;

      // Track which fields actually have overrides from the backend (not null)
      const actualOverrides: { [key: string]: boolean } = {};

      // Process each item in the zone group
      zoneItems.forEach(item => {
        const dayKey = dayMapping[item.day_num as keyof typeof dayMapping];
        
        if (dayKey) {
          // Map default percentage values (convert to percentage format with 2 decimal places)
          percentages[dayKey].default = item.default_pct !== null && item.default_pct !== undefined 
            ? `${(item.default_pct * 100).toFixed(2)}%`
            : '0.00%';
          
          // For override: if null, copy from default; if not null, it's an actual backend override
          if (item.override_pct !== null && item.override_pct !== undefined) {
            percentages[dayKey].override = `${(item.override_pct * 100).toFixed(2)}%`;
            actualOverrides[dayKey] = true; // Mark as actual override from backend
          } else {
            percentages[dayKey].override = percentages[dayKey].default; // Copy default to override when null
            actualOverrides[dayKey] = false; // Not an actual override
          }
        }

        // For KCPH, we'll use the first item's values (assuming consistent across days for a zone)
        if (item.default_kcph !== undefined && item.default_kcph !== null) {
          kcph.default = item.default_kcph.toFixed(2);
        } else {
          kcph.default = '0.00';
        }
        
        // For KCPH override: if null, copy from default; if not null, it's an actual backend override
        if (item.override_kcph !== undefined && item.override_kcph !== null) {
          kcph.override = item.override_kcph.toFixed(2);
          actualOverrides['kcph'] = true; // Mark as actual override from backend
        } else {
          kcph.override = kcph.default; // Copy default to override when null
          actualOverrides['kcph'] = false; // Not an actual override
        }
      });

      return {
        id: zoneId,
        zoneName: zoneName,
        percentages,
        kcph,
        __actualOverrides: actualOverrides // Track which overrides came from backend
      } as ZoneRow;
    });
};